import { IPromise } from 'angular';
import * as React from 'react';
import { connect } from 'react-redux';
import * as _ from 'underscore';

import { DialogType, DialogPositioningBehavior } from '@tableau/widgets';

import { ReactModalOptions, ModalInstance, ReactModalService } from '../../../ts/services/ReactModalService';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { AppState } from '../../state/AppState';
import { useIsMounted } from '../../hooks';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { ExplorableItemType, isSpaceInfoItemType } from '../../../ts/models/ContentType';
import { ContentSpaceDefinitions } from '../../../ts/models/ContentDefinitions';

const DIALOG_WIDTH = 850;
export const ACTION_DIALOG_TESTID = 'content-detail-dialog';

export namespace ContentDetailsDialog {

  interface ContentsOwnProps {
    readonly children: React.ReactNode;
    readonly confirmCloseDialog: () => void;
  }

  interface ContentsStateProps {
    readonly routerReactKey: number;
  }

  type ContentsProps = ContentsOwnProps & ContentsStateProps;

  const Contents = (props: ContentsProps) => {
    const isMounted = useIsMounted();
    React.useEffect(() => {
      if (isMounted) {
        // The routerReactKey is used in high level HashRouters and triggers rerenders if key changes.
        // We use this to refresh all content in a page without doing a page reload.
        // So we know if an action change was successful if this key has changed and so close the dialog.
        props.confirmCloseDialog();
      }
    }, [props.routerReactKey]);

    return (
      <div style={{display: 'flex', flexDirection: 'column'}}>
       {props.children}
      </div>
    );
  };

  const mapStateToProps: MapStateToProps<ContentsStateProps> = (state: AppState) => ({
    routerReactKey: state.routerSpaceReactKey
  });

  export const ConnectedContents: React.ComponentClass<ContentsOwnProps> =
    connect(mapStateToProps)(Contents);

  interface OptionsProps {
    readonly content: IContentInfo<ExplorableItemType>;
    readonly confirmCloseDialog: () => void;
  }
  export function Options(props: OptionsProps): ReactModalOptions {

    const contentType = props.content.contentType;
    let dialogTitle = '';
    let dialogBody: any = undefined;
    if (isSpaceInfoItemType(contentType)) {
      const contentDefinition = ContentSpaceDefinitions[contentType];
      dialogTitle = contentDefinition.detailDialogTitle();
      dialogBody = contentDefinition.detaillDialogBody;
    }

    if (dialogTitle.length === 0 || _.isUndefined(dialogBody)) {
      throw new Error(contentType + ' is not supported for react detail dialog.');
    }

    return {
      title: dialogTitle,
      testId: ACTION_DIALOG_TESTID,
      type: DialogType.NormalWithTitleBar,
      showCloseButton: true,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      dialogWidth: DIALOG_WIDTH,
      dialogStyle: {minWidth: DIALOG_WIDTH + 'px'},
      dialogBodyStyle: {width: DIALOG_WIDTH + 'px'},
      dialogContent:
        <ConnectedContents confirmCloseDialog={props.confirmCloseDialog}>
          {dialogBody(props.content)}
        </ConnectedContents>,
      dialogContentStyle: { padding: 0 },
    };
  }

  export function open(content: IContentInfo<ExplorableItemType>): IPromise<any> {
    let modalInstance: ModalInstance;

    const confirmCloseDialog = (): void => {
      modalInstance && modalInstance.close('confirm');
    };
    const optionProps: OptionsProps = {
      content,
      confirmCloseDialog
    };
    modalInstance = ReactModalService.open(Options(optionProps));
    return modalInstance.result;
  }
}
